import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/client";

// Mirrors BasketsTab's last-pledge warning (same trigger: releasing the only
// active pledge leaves nothing left for LTV to be computed against) — reworded
// to drop "במסגרת" since this section's singleton path must never say the
// word "מסגרת" to the user.
const LAST_PLEDGE_CONFIRM =
  "זהו השעבוד האחרון — לאחר השחרור לא יהיה בסיס לחישוב LTV. להמשיך?";

const num = (v) => (v === "" || v == null ? undefined : Number(v));

const NEW_ASSET = "__new__";
const ASSET_TYPES = [
  { value: "STOCK", label: "מניות" },
  { value: "REAL_ESTATE", label: 'נדל"ן' },
  { value: "LAND", label: "קרקע" },
  { value: "DEVELOPMENT_PROJECT", label: "פרויקט בפיתוח" },
  { value: "OTHER", label: "אחר" },
];
const EMPTY_NEW_ASSET = { assetType: "LAND", assetName: "", symbol: "", totalUnits: "", value: "", owningCompany: "" };
const EMPTY_PLEDGE = { assetId: "", pledgedUnits: "", lienPosition: "FIRST", lienHolder: "", newAsset: EMPTY_NEW_ASSET };
const EMPTY_GUARANTEE = { guaranteeType: "PERSONAL", guarantorName: "", amount: "", expiryDate: "" };

/**
 * Edit a pledge in place. Releasing and re-adding was the only way to correct a
 * typo in the pledged amount, which loses the pledge's history and, for stock,
 * briefly frees units that were never actually released.
 */
function EditPledgeRow({ pledge, onCancel, onSaved }) {
  const isStock = pledge.assetId?.assetType === "STOCK";
  const [form, setForm] = useState({
    pledgedUnits: pledge.pledgedUnits ?? "",
    lienPosition: pledge.lienPosition || "FIRST",
    lienHolder: pledge.lienHolder || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await apiClient.put(`/pledges/${pledge._id}`, {
        ...(isStock ? { pledgedUnits: num(form.pledgedUnits) } : {}),
        lienPosition: form.lienPosition || undefined,
        lienHolder: form.lienHolder || undefined,
      });
      onSaved();
    } catch (e) {
      const msg = e.response?.data?.error || "העדכון נכשל";
      setError(msg === "OVER_PLEDGED"
        ? "הכמות גדולה מהמניות הזמינות לאחר שעבודים והגבלות."
        : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid var(--border-light)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 140, fontSize: 13, paddingBottom: 8 }}>
          {pledge.assetId?.assetName || "נכס"}{pledge.assetId?.symbol ? ` · ${pledge.assetId.symbol}` : ""}
        </span>
        {isStock && (
          <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
            <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>מניות משועבדות</label>
            <input className="input" type="number" min="1" value={form.pledgedUnits}
              onChange={(e) => setForm((f) => ({ ...f, pledgedUnits: e.target.value }))} />
          </div>
        )}
        <div className="field" style={{ minWidth: 110, marginBottom: 0 }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>דרגת שעבוד</label>
          <select className="select" value={form.lienPosition}
            onChange={(e) => setForm((f) => ({ ...f, lienPosition: e.target.value }))}>
            <option value="FIRST">ראשון</option><option value="SECOND">שני</option>
            <option value="THIRD">שלישי</option><option value="OTHER">אחר</option>
          </select>
        </div>
        <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>גוף משעבד</label>
          <input className="input" value={form.lienHolder}
            onChange={(e) => setForm((f) => ({ ...f, lienHolder: e.target.value }))} />
        </div>
        <button type="button" className="btn btn-primary" style={{ width: "auto" }} disabled={saving} onClick={save}>
          {saving ? "שומר…" : "שמור"}
        </button>
        <button type="button" className="btn btn-ghost" style={{ width: "auto" }} disabled={saving} onClick={onCancel}>
          ביטול
        </button>
      </div>
      {error && <div className="message-error" style={{ marginTop: 6, fontSize: 13 }}>{error}</div>}
    </div>
  );
}

/**
 * Add a pledge or guarantee straight from the loan page.
 *
 * A standalone loan's basket is a singleton and never appears in the baskets tab,
 * so before this the only way to attach collateral was at loan-creation time — after
 * that the loan's security was effectively read-only. Assets must already exist in
 * the registry: creating one inline is the assets page's job, and duplicating that
 * form here is how the same stock ends up in the registry twice.
 */
function AddSecurityPanel({ basketId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("PLEDGE");
  const [holdings, setHoldings] = useState([]);
  const [pledge, setPledge] = useState(EMPTY_PLEDGE);
  const [guarantee, setGuarantee] = useState(EMPTY_GUARANTEE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    apiClient.get("/asset-holdings").then((r) => setHoldings(r.data || [])).catch(() => {});
  }, [open]);

  const creatingAsset = pledge.assetId === NEW_ASSET;
  const selected = holdings.find((h) => h._id === pledge.assetId);
  const isStock = creatingAsset
    ? pledge.newAsset.assetType === "STOCK"
    : selected?.assetType === "STOCK";
  const setNA = (field, value) =>
    setPledge((p) => ({ ...p, newAsset: { ...p.newAsset, [field]: value } }));

  const reset = () => {
    setPledge(EMPTY_PLEDGE);
    setGuarantee(EMPTY_GUARANTEE);
    setError("");
    setOpen(false);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (kind === "PLEDGE") {
        await apiClient.post("/pledges", {
          basketId,
          ...(creatingAsset
            ? {
                newAsset: {
                  assetType: pledge.newAsset.assetType,
                  assetName: pledge.newAsset.assetName.trim(),
                  owningCompany: pledge.newAsset.owningCompany || undefined,
                  ...(isStock
                    ? { symbol: pledge.newAsset.symbol, totalUnits: num(pledge.newAsset.totalUnits) }
                    : { value: num(pledge.newAsset.value) }),
                },
              }
            : { assetId: pledge.assetId }),
          pledgedUnits: isStock ? num(pledge.pledgedUnits) : undefined,
          lienPosition: pledge.lienPosition || undefined,
          lienHolder: pledge.lienHolder || undefined,
        });
      } else {
        await apiClient.post("/guarantees", {
          basketId,
          guaranteeType: guarantee.guaranteeType,
          guarantorName: guarantee.guarantorName || undefined,
          amount: num(guarantee.amount),
          expiryDate: guarantee.expiryDate || undefined,
        });
      }
      reset();
      onAdded?.();
    } catch (e) {
      const msg = e.response?.data?.error || "ההוספה נכשלה";
      // The over-pledge guard is the one users hit most; say what it means.
      setError(msg === "OVER_PLEDGED"
        ? "אין מספיק מניות זמינות בנכס לאחר שעבודים והגבלות."
        : msg);
    } finally {
      setSaving(false);
    }
  };

  const assetChosen = creatingAsset
    ? Boolean(pledge.newAsset.assetName.trim()) && (isStock ? Boolean(pledge.newAsset.symbol.trim()) : Number(pledge.newAsset.value) > 0)
    : Boolean(pledge.assetId);
  const canSave = kind === "PLEDGE"
    ? assetChosen && (!isStock || Number(pledge.pledgedUnits) > 0)
    : Number(guarantee.amount) > 0;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          width: "100%", padding: "12px 16px", marginBottom: "var(--space-sm)", cursor: "pointer",
          border: "2px dashed var(--accent)", borderRadius: "var(--radius-sm)",
          background: "var(--bg-subtle)", color: "var(--accent)",
          fontSize: 14, fontWeight: 600, fontFamily: "inherit",
        }}
      >
        + הוסף בטוחה או ערבות
      </button>
    );
  }

  return (
    <div style={{ border: "1px solid var(--border-light)", borderRadius: "var(--radius-sm)", padding: "12px 14px", background: "var(--bg-subtle)", marginBottom: "var(--space-sm)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סוג</label>
          <select className="select" value={kind} onChange={(e) => { setKind(e.target.value); setError(""); }}>
            <option value="PLEDGE">שעבוד נכס</option>
            <option value="GUARANTEE">ערבות</option>
          </select>
        </div>

        {kind === "PLEDGE" ? (
          <>
            <div className="field" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>נכס</label>
              <select className="select" value={pledge.assetId}
                onChange={(e) => setPledge((p) => ({ ...p, assetId: e.target.value, pledgedUnits: "" }))}>
                <option value="">— בחר נכס —</option>
                <option value={NEW_ASSET}>+ נכס חדש…</option>
                {holdings.map((h) => (
                  <option key={h._id} value={h._id}>
                    {h.assetName}{h.symbol ? ` · ${h.symbol}` : ""}{h.owningCompany ? ` · ${h.owningCompany}` : ""}
                  </option>
                ))}
              </select>
            </div>
            {isStock && (
              <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>מניות לשעבוד</label>
                <input className="input" type="number" min="1" value={pledge.pledgedUnits}
                  onChange={(e) => setPledge((p) => ({ ...p, pledgedUnits: e.target.value }))} />
              </div>
            )}
            <div className="field" style={{ minWidth: 110, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>דרגת שעבוד</label>
              <select className="select" value={pledge.lienPosition}
                onChange={(e) => setPledge((p) => ({ ...p, lienPosition: e.target.value }))}>
                <option value="FIRST">ראשון</option><option value="SECOND">שני</option>
                <option value="THIRD">שלישי</option><option value="OTHER">אחר</option>
              </select>
            </div>
            <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>גוף משעבד</label>
              <input className="input" value={pledge.lienHolder} placeholder="בנק"
                onChange={(e) => setPledge((p) => ({ ...p, lienHolder: e.target.value }))} />
            </div>

            {creatingAsset && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", width: "100%" }}>
                <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סוג נכס</label>
                  <select className="select" value={pledge.newAsset.assetType}
                    onChange={(e) => setNA("assetType", e.target.value)}>
                    {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="field" style={{ flex: 1, minWidth: 150, marginBottom: 0 }}>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שם הנכס</label>
                  <input className="input" value={pledge.newAsset.assetName}
                    onChange={(e) => setNA("assetName", e.target.value)} />
                </div>
                {isStock ? (
                  <>
                    <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סמל</label>
                      <input className="input" dir="ltr" placeholder="DLEKG.TA" value={pledge.newAsset.symbol}
                        onChange={(e) => setNA("symbol", e.target.value)} />
                    </div>
                    <div className="field" style={{ minWidth: 130, marginBottom: 0 }}>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סה"כ מניות בבעלות</label>
                      <input className="input" type="number" value={pledge.newAsset.totalUnits}
                        placeholder="כמות המשועבדת" onChange={(e) => setNA("totalUnits", e.target.value)} />
                    </div>
                  </>
                ) : (
                  <div className="field" style={{ minWidth: 140, marginBottom: 0 }}>
                    <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שווי (₪)</label>
                    <input className="input" type="number" value={pledge.newAsset.value}
                      onChange={(e) => setNA("value", e.target.value)} />
                  </div>
                )}
                <div className="field" style={{ minWidth: 150, marginBottom: 0 }}>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>חברה מחזיקה</label>
                  <input className="input" value={pledge.newAsset.owningCompany}
                    onChange={(e) => setNA("owningCompany", e.target.value)} />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="field" style={{ minWidth: 120, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סוג ערבות</label>
              <select className="select" value={guarantee.guaranteeType}
                onChange={(e) => setGuarantee((g) => ({ ...g, guaranteeType: e.target.value }))}>
                <option value="PERSONAL">אישית</option><option value="CORPORATE">תאגידית</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שם ערב</label>
              <input className="input" value={guarantee.guarantorName}
                onChange={(e) => setGuarantee((g) => ({ ...g, guarantorName: e.target.value }))} />
            </div>
            <div className="field" style={{ minWidth: 130, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סכום (₪)</label>
              <input className="input" type="number" value={guarantee.amount}
                onChange={(e) => setGuarantee((g) => ({ ...g, amount: e.target.value }))} />
            </div>
            <div className="field" style={{ minWidth: 140, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>תוקף</label>
              <input className="input" type="date" value={guarantee.expiryDate}
                onChange={(e) => setGuarantee((g) => ({ ...g, expiryDate: e.target.value }))} />
            </div>
          </>
        )}

        <button type="button" className="btn btn-primary" style={{ width: "auto" }}
          disabled={saving || !canSave} onClick={save}>
          {saving ? "שומר…" : "הוסף"}
        </button>
        <button type="button" className="btn btn-ghost" style={{ width: "auto" }}
          disabled={saving} onClick={reset}>
          ביטול
        </button>
      </div>

      {kind === "PLEDGE" && holdings.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8 }}>
          אין נכסים במרשם. הוסיפו נכס בדף הנכסים ואז שעבדו אותו כאן.
        </div>
      )}
      {error && <div className="message-error" style={{ marginTop: 8, fontSize: 13 }}>{error}</div>}
    </div>
  );
}

/**
 * Renders a loan's security (pledges/guarantees + LTV thresholds) in place
 * on the loan detail page.
 *
 * - Singleton basket (the common case — every standalone loan owns a private
 *   basket under the hood): thresholds are editable inline and pledges/
 *   guarantees are listed and releasable right here. The word "מסגרת" never
 *   appears — to the user this IS the loan's security, not a shared basket.
 * - Named/shared basket: read-only summary + a link to the collateral page,
 *   where the basket (and everything shared across its member loans) is
 *   actually managed.
 */
export default function LoanSecuritySection({ basket, canEdit, onSecurityChanged }) {
  const [pledges, setPledges] = useState([]);
  const [guarantees, setGuarantees] = useState([]);
  const [memberCount, setMemberCount] = useState(1);
  const [editingPledgeId, setEditingPledgeId] = useState(null);
  const [thresholds, setThresholds] = useState({
    ltvWarningThreshold: basket?.ltvWarningThreshold ?? "",
    ltvBreachThreshold: basket?.ltvBreachThreshold ?? "",
  });
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    if (!basket?._id) return;
    apiClient.get(`/pledges?basketId=${basket._id}&status=ACTIVE`).then((r) => setPledges(r.data || [])).catch(() => {});
    apiClient.get(`/guarantees?basketId=${basket._id}&status=ACTIVE`).then((r) => setGuarantees(r.data || [])).catch(() => {});
    apiClient.get(`/baskets/${basket._id}`).then((r) => setMemberCount((r.data?.loans || []).length || 1)).catch(() => {});
  }, [basket?._id]);

  useEffect(refresh, [refresh]);

  // Re-sync the threshold form whenever a fresh basket prop arrives (e.g. the
  // parent refetches the loan after onSecurityChanged fires) so the form never
  // keeps showing values that were superseded elsewhere.
  useEffect(() => {
    setThresholds({
      ltvWarningThreshold: basket?.ltvWarningThreshold ?? "",
      ltvBreachThreshold: basket?.ltvBreachThreshold ?? "",
    });
  }, [basket?._id, basket?.ltvWarningThreshold, basket?.ltvBreachThreshold]);

  if (!basket?._id) return null;

  const shared = !basket.isSingleton;

  const saveThresholds = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/baskets/${basket._id}`, {
        ltvWarningThreshold: num(thresholds.ltvWarningThreshold),
        ltvBreachThreshold: num(thresholds.ltvBreachThreshold),
      });
      onSecurityChanged?.();
    } catch (e) {
      alert(e.response?.data?.error || "שמירת הספים נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const releasePledge = async (id) => {
    const isLast = pledges.length === 1;
    if (!window.confirm(isLast ? LAST_PLEDGE_CONFIRM : "לשחרר את השעבוד?")) return;
    try {
      await apiClient.delete(`/pledges/${id}`);
      // Shrink local state immediately — a fast second release needs the true
      // count without waiting on the refresh() round-trip below.
      setPledges((prev) => prev.filter((p) => p._id !== id));
      refresh();
      onSecurityChanged?.();
    } catch (e) {
      alert(e.response?.data?.error || "שחרור נכשל");
    }
  };

  const releaseGuarantee = async (id) => {
    if (!window.confirm("לשחרר את הערבות?")) return;
    try {
      await apiClient.delete(`/guarantees/${id}`);
      setGuarantees((prev) => prev.filter((g) => g._id !== id));
      refresh();
      onSecurityChanged?.();
    } catch (e) {
      alert(e.response?.data?.error || "שחרור נכשל");
    }
  };

  return (
    <div className="info-field full">
      <h3 style={{ margin: "0 0 var(--space-xs) 0", fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
        בטחונות וערבויות
      </h3>

      {shared && (
        <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: "var(--space-sm)" }}>
          מנוהל במסגרת <b>{basket.name}</b> — משותף ל-{memberCount} הלוואות.{" "}
          <Link to="/collateral" style={{ color: "var(--accent)", textDecoration: "none", borderBottom: "1px solid var(--accent)" }}>
            עריכה בדף הבטחונות ←
          </Link>
        </div>
      )}

      {!shared && canEdit && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: "var(--space-sm)" }}>
          {[
            ["ltvWarningThreshold", "סף אזהרה LTV (%)"],
            ["ltvBreachThreshold", "סף הפרה LTV (%)"],
          ].map(([key, label]) => (
            <div className="field" key={key} style={{ minWidth: 120, marginBottom: 0 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>{label}</label>
              <input
                className="input"
                type="number"
                min="0"
                max="200"
                step="0.1"
                value={thresholds[key]}
                onChange={(e) => setThresholds((t) => ({ ...t, [key]: e.target.value }))}
              />
            </div>
          ))}
          <button
            type="button"
            className="input"
            style={{ width: "auto", cursor: "pointer", padding: "6px 16px" }}
            disabled={saving}
            onClick={saveThresholds}
          >
            {saving ? "שומר…" : "שמירת ספים"}
          </button>
        </div>
      )}

      {!shared && canEdit && (
        <AddSecurityPanel basketId={basket._id} onAdded={() => { refresh(); onSecurityChanged?.(); }} />
      )}

      <div style={{ fontSize: 13, fontWeight: 600, margin: "6px 0" }}>שעבודים</div>
      {pledges.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>אין שעבודים פעילים.</div>
      )}
      {pledges.map((p) => (
        editingPledgeId === p._id ? (
          <EditPledgeRow
            key={p._id}
            pledge={p}
            onCancel={() => setEditingPledgeId(null)}
            onSaved={() => { setEditingPledgeId(null); refresh(); onSecurityChanged?.(); }}
          />
        ) : (
          <div
            key={p._id}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}
          >
            <span style={{ flex: 1 }}>
              {p.assetId?.assetName || "נכס"}{p.assetId?.symbol ? ` · ${p.assetId.symbol}` : ""}
              {" · "}{p.pledgedUnits ? `${Number(p.pledgedUnits).toLocaleString()} מניות` : "נכס בשלמותו"}
              {p.lienHolder ? ` · ${p.lienHolder}` : ""}
            </span>
            {!shared && canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => setEditingPledgeId(p._id)}
                  style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer" }}
                >
                  ערוך
                </button>
                <button
                  type="button"
                  onClick={() => releasePledge(p._id)}
                  style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer" }}
                >
                  שחרר
                </button>
              </>
            )}
          </div>
        )
      ))}

      <div style={{ fontSize: 13, fontWeight: 600, margin: "12px 0 6px" }}>ערבויות</div>
      {guarantees.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>אין ערבויות פעילות.</div>
      )}
      {guarantees.map((g) => (
        <div
          key={g._id}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}
        >
          <span style={{ flex: 1 }}>
            {g.guaranteeType === "PERSONAL" ? "אישית" : "תאגידית"} · {g.guarantorName || "—"} · ₪{Number(g.amount).toLocaleString()}
            {g.expiryDate ? ` · תוקף ${new Date(g.expiryDate).toLocaleDateString("he-IL")}` : ""}
          </span>
          {!shared && canEdit && (
            <button
              type="button"
              onClick={() => releaseGuarantee(g._id)}
              style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer" }}
            >
              שחרר
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
